<template>
  <div class="visualisasi-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">PETA PENGETAHUAN</p>
        <h2>Visualisasi Ontologi Wayang Purwa</h2>
        <p class="page-intro">Jelajahi hubungan antarkelas dalam basis pengetahuan wayang.</p>
      </div>
      <div class="graph-meta" aria-label="Ringkasan graph">
        <span class="meta-value">{{ nodes.length }}</span>
        <span class="meta-label">kelas</span>
        <span class="meta-divider" aria-hidden="true"></span>
        <span class="meta-value">{{ edges.length }}</span>
        <span class="meta-label">relasi</span>
      </div>
    </header>

    <section class="graph-panel">
      <div class="graph-toolbar">
        <span class="toolbar-label"><span class="status-dot"></span>Graph ontologi</span>
        <span class="toolbar-hint">Pilih node untuk melihat detail</span>
      </div>
      <div id="cy" class="network" aria-label="Graph relasi ontologi"></div>
    </section>

    <section v-if="selectedNode" class="detail">
      <div class="detail-heading">
        <span class="detail-kicker">KELAS TERPILIH</span>
        <h3>{{ selectedNode.label }}</h3>
      </div>
      <div class="detail-fields">
        <div class="detail-field">
          <span>Nama class</span>
          <strong>{{ selectedNode.id }}</strong>
        </div>
        <div class="detail-field">
          <span>Group</span>
          <strong>{{ selectedNode.group || 'Tidak tersedia' }}</strong>
        </div>
      </div>
      <p v-if="selectedNode.comment" class="description">{{ selectedNode.comment }}</p>

      <div v-if="selectedNode.instances && selectedNode.instances.length">
        <span class="instances-label">Individu terkait</span>
        <ul class="instances-list">
          <li v-for="(ind, i) in selectedNode.instances" :key="i">{{ ind }}</li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import cytoscape from 'cytoscape'
import { nodes, edges } from '@/data/ontologyClasses'

const selectedNode = ref(null)

onMounted(() => {
  const nodeIds = nodes.map(n => n.data.id)

  const filteredEdges = edges.filter(e =>
    nodeIds.includes(e.data.source) && nodeIds.includes(e.data.target)
  )

  const cy = cytoscape({
    container: document.getElementById('cy'),
    elements: {
      nodes,
      edges: filteredEdges
    },
    style: [
      {
        selector: 'node',
        style: {
          'label': 'data(label)',
          'text-valign': 'center',
          'text-halign': 'center',
          'color': '#fffaf0',
          'background-color': '#9a5b22',
          'border-color': '#673b1a',
          'border-width': 2,
          'font-family': 'Plus Jakarta Sans',
          'font-size': 13,
          'font-weight': 700,
          'text-wrap': 'wrap',
          'text-max-width': 110,
          'width': 116,
          'height': 56,
          'shape': 'roundrectangle',
          'padding': 8
        }
      },
      {
        selector: 'node:selected',
        style: {
          'background-color': '#d97706',
          'border-color': '#4a260f',
          'border-width': 4,
          'overlay-color': '#f59e0b',
          'overlay-opacity': 0.14
        }
      },
      {
        selector: 'edge',
        style: {
          'label': 'data(label)',
          'width': 2,
          'line-color': '#c58a4b',
          'target-arrow-color': '#9a5b22',
          'target-arrow-shape': 'triangle',
          'curve-style': 'bezier',
          'color': '#6b421e',
          'font-family': 'Plus Jakarta Sans',
          'font-size': 10,
          'font-weight': 700,
          'text-background-color': '#fffaf0',
          'text-background-opacity': 1,
          'text-background-padding': 3,
          'text-rotation': 'autorotate',
          'text-margin-y': -10
        }
      }
    ],
    layout: {
      name: 'cose',
      animate: true
    }
  })

  cy.on('tap', 'node', (evt) => {
    const node = evt.target.data()
    selectedNode.value = node
  })

  cy.on('tap', (evt) => {
    if (evt.target === cy) {
      selectedNode.value = null
    }
  })
})
</script>

<style scoped>
.visualisasi-page {
  min-height: 100vh;
  padding: clamp(28px, 5vw, 64px) clamp(20px, 5vw, 72px);
  background:
    radial-gradient(circle at 92% 5%, rgba(239, 184, 92, 0.24), transparent 28%),
    #fff9ed;
}

.page-header {
  max-width: 1240px;
  margin: 0 auto 24px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 24px;
}

.eyebrow,
.detail-kicker {
  color: #9a5b22;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  margin: 0 0 8px;
}

.page-header h2 {
  color: #3c2414;
  font-size: clamp(24px, 3vw, 38px);
  line-height: 1.15;
  margin: 0;
}

.page-intro {
  color: #76563a;
  margin: 10px 0 0;
}

.graph-meta {
  display: flex;
  align-items: baseline;
  gap: 7px;
  white-space: nowrap;
  color: #76563a;
}

.meta-value {
  color: #9a5b22;
  font-size: 22px;
  font-weight: 800;
}

.meta-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.meta-divider {
  width: 1px;
  height: 24px;
  background: #e5c79e;
  margin: 0 10px;
}

.graph-panel,
.detail {
  max-width: 1240px;
  margin: 0 auto;
  background: rgba(255, 253, 247, 0.9);
  border: 1px solid #ead3ad;
  border-radius: 14px;
  box-shadow: 0 18px 40px rgba(95, 61, 20, 0.1);
  overflow: hidden;
}

.graph-toolbar {
  min-height: 52px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #efdfc4;
  color: #76563a;
  font-size: 12px;
}

.toolbar-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  color: #3c2414;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #c56a28;
  box-shadow: 0 0 0 4px #f9e5c5;
}

.network {
  height: min(68vh, 680px);
  min-height: 480px;
  background-color: #fffdf7;
  background-image: radial-gradient(#ead8ba 1px, transparent 1px);
  background-size: 22px 22px;
}

.detail {
  margin-top: 24px;
  padding: 22px 24px;
}

.detail-heading h3 {
  color: #3c2414;
  font-size: 22px;
  margin: 0;
}

.detail-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 48px;
  margin-top: 18px;
}

.detail-field {
  display: grid;
  gap: 3px;
  min-width: 180px;
}

.detail-field span,
.instances-label {
  color: #9a6c42;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.detail-field strong {
  color: #573618;
  font-size: 14px;
}

.description {
  color: #76563a;
  margin: 18px 0 0;
  max-width: 760px;
}

.instances-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
}

.instances-list li {
  background: #fff0d3;
  border: 1px solid #e7c58f;
  border-radius: 999px;
  color: #6b421e;
  font-size: 13px;
  padding: 5px 11px;
}

@media (max-width: 640px) {
  .page-header {
    align-items: start;
    flex-direction: column;
  }

  .graph-toolbar {
    align-items: start;
    flex-direction: column;
    gap: 4px;
    justify-content: center;
    padding: 12px 16px;
  }

  .network {
    min-height: 410px;
    height: 62vh;
  }

  .detail {
    padding: 20px;
  }
}
</style>
